import styled, { css, CSSProp } from "styled-components"
import { Link as GatsbyLink, GatsbyLinkProps } from "gatsby"
import { ElementProps, elementCss } from "legacy/components/shared/base"

function sharedStyles({ theme }: StyledLinkProps): CSSProp {
  return css`
    border: none;
    cursor: pointer;
    display: inline-block;
    font-family: ${theme.fonts.martel};
    text-decoration: none;
    text-transform: uppercase;

    /* :before element is required to adjust for Martel Sans' baseline  */
    /* If font-face is changed, this will likely need to change as well */
    &:before {
      content: "";
      height: 0;
      padding-top: ${theme.space.lineHeightOffset};
      display: block;
    }
  `
}

function primaryStyles({ theme, variant }: StyledLinkProps): CSSProp {
  if (!variant || variant === "primary") {
    return css`
      border-radius: ${theme.radii.button};
      max-width: 100%;
      min-width: 266px;
      text-align: center;
      padding: ${theme.space.sm};

      /* Default outline does not have rounded corners         */
      /* We disable it here and use the :after element instead */
      &:focus {
        outline: none;
      }

      &:focus:after {
        @media (max-width: 40em) {
          bottom: -4px;
          left: -4px;
          right: -4px;
          top: -4px;
        }

        border-radius: ${theme.radii.focus};
        border: ${theme.borders.focus};
        bottom: -5px;
        box-shadow: ${theme.shadows.focus};
        content: "";
        display: "block";
        left: -5px;
        position: absolute;
        right: -5px;
        top: -5px;
        z-index: 1;
      }
    `
  }

  return css``
}

function variantStyles({ theme, variant }: StyledLinkProps): CSSProp {
  const variantKey = variant === "secondary" ? "secondary" : "primarySky"

  return css`
    background: ${theme.colors.buttonBackground[variantKey]};
    border: ${theme.borders.button[variantKey]};
    color: ${theme.colors.buttonText[variantKey]};
    font-size: ${theme.fontSizes.button[variantKey]};
    font-weight: ${theme.fontWeights.button[variantKey]};
    letter-spacing: ${theme.letterSpacings.button[variantKey]};

    &:hover {
      background: ${theme.colors.buttonHoverBackground[variantKey]};
      border: ${theme.borders.buttonHover[variantKey]};
      color: ${theme.colors.buttonHoverText[variantKey]};
    }
  `
}

function secondaryStyles({ theme, variant }: StyledLinkProps): CSSProp {
  if (variant === "secondary") {
    return css`
      line-height: 28px;
      padding: 0;
      text-align: left;
      text-underline-offset: ${theme.space.xs};
      transition: all 150ms ease;

      &:hover {
        text-decoration: underline;
        text-underline-offset: ${theme.space.sm};
        text-underline-position: left;
        text-decoration-color: ${theme.colors.sky};
      }
    `
  }

  return css``
}

type LinkVariant = "primary" | "secondary"

interface OwnProps {
  variant?: LinkVariant
}

export type StyledLinkProps = OwnProps & ElementProps

export const StyledLink = styled.a<StyledLinkProps>`
  ${sharedStyles}
  ${variantStyles}
  ${primaryStyles}
  ${secondaryStyles}
  ${elementCss}
`

export type StyledGatsbyLinkProps = StyledLinkProps & GatsbyLinkProps<{}>

export const StyledGatsbyLink = styled(GatsbyLink)<StyledGatsbyLinkProps>`
  ${sharedStyles}
  ${variantStyles}
  ${primaryStyles}
  ${secondaryStyles}
  ${elementCss}
`
